import React, { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { IconChevronDown, IconRotateClockwise, IconTrash } from '@tabler/icons-react'

import { formatDate, parseServerDate } from '@helpers/utils'
import { useAnchoredPosition } from '@helpers/hooks/useAnchoredPosition'
import { useHasPermission } from '@helpers/hooks/useHasPermission'
import { Perm } from '@type/permissions'
import { getInviteStatus } from '@pages/Team/Members/List/components/Invites/utils'
// Both surfaces that act on an invite (this chip and the pending-invites
// table) route through the same confirmation modals, so resending from the
// roster row asks exactly what resending from the table below it asks.
import ResendInvitePopup from '@pages/Team/Members/List/components/Invites/ResendInvitePopup'
import RevokeInvitePopup from '@pages/Team/Members/List/components/Invites/RevokeInvitePopup'
// Palette/icons come from the badge module so this chip and the status column
// in the pending-invites table can't drift apart — they describe the same
// invite. Only the wording and the button chrome differ here.
import {
  INVITE_STATUS_ACCENT,
  INVITE_STATUS_CHIP_BASE,
  INVITE_STATUS_HOVER,
  INVITE_STATUS_ICONS,
  INVITE_STATUS_STYLES,
} from '@pages/Team/Members/List/components/Invites/InviteStatusBadge'

import type { OrgStructureRecord } from '@pages/Team/Members/types'

const POPOVER_WIDTH = 256

export interface InvitedCellProps {
  record: OrgStructureRecord
}

// "Invite sent" Access cell. Same chip-style + popover pattern as
// NotInvitedCell and RoleChips so the column reads as one consistent
// interaction. Click → popover with Resend / Revoke actions.
export const InvitedCell: React.FC<InvitedCellProps> = ({ record }) => {
  const { t } = useTranslation('org')
  // Resend / Revoke are invite mutations — gate on TEAM_INVITE_MEMBER, matching
  // NotInvitedCell and the pending-invites table. Without this, a
  // manage_member_roles-only user (who reaches this cell via the Role column)
  // saw the actions but the backend 404s them. Read-only badge otherwise.
  const canInvite = useHasPermission(Perm.TEAM_INVITE_MEMBER)
  const [popoverOpen, setPopoverOpen] = useState(false)
  // Which confirmation modal is open, if any. Held here rather than in the
  // popover so the modal survives the popover closing behind it.
  const [confirming, setConfirming] = useState<'resend' | 'revoke' | null>(null)
  // UUID comes straight off the OrgStructure row now (was previously
  // looked up via a separate, infinitely-cached invites query, which
  // silently disabled the Resend / Revoke buttons when the cache
  // didn't include the row's invite).
  const inviteUuid = record.invite_uuid ?? undefined
  // `is_invited` (and therefore this cell) covers every unused invite, expired
  // ones included — so without this the chip claimed "Invite sent" for an
  // invitation whose link had already stopped working, which is exactly the
  // blind spot admins were filing support tickets about.
  const status = getInviteStatus(record.invite_expires_at)
  const isExpired = status === 'expired'
  const StatusIcon = INVITE_STATUS_ICONS[status]
  const label = isExpired ? t('invitedCell.expiredLabel') : t('invitedCell.sentLabel')

  // Trigger ref drives the portaled popover's viewport-relative
  // position (escapes the table's overflow clip).
  const triggerRef = useRef<HTMLButtonElement>(null)

  const handleToggle = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    setPopoverOpen((o) => !o)
  }, [])

  // Picking an action dismisses the menu and hands off to its modal.
  const handleAction = useCallback((action: 'resend' | 'revoke') => {
    setPopoverOpen(false)
    setConfirming(action)
  }, [])

  if (!canInvite) {
    // Read-only badge — no resend/revoke affordance.
    return (
      <span
        className={`${INVITE_STATUS_CHIP_BASE} ${INVITE_STATUS_STYLES[status]}`}
        data-qa="invite-sent-cell"
        // `data-qa` stays "invite-sent-cell" for both states so existing
        // selectors keep resolving; this carries which state is rendered.
        data-invite-status={status}
      >
        <StatusIcon size={12} />
        {label as string}
      </span>
    )
  }

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={handleToggle}
        className={`${INVITE_STATUS_CHIP_BASE} ${INVITE_STATUS_STYLES[status]} ${INVITE_STATUS_HOVER[status]}`}
        data-qa="invite-sent-cell"
        // `data-qa` stays "invite-sent-cell" for both states so existing
        // selectors keep resolving; this carries which state is rendered.
        data-invite-status={status}
      >
        <StatusIcon size={12} />
        {label as string}
        <IconChevronDown size={12} className={INVITE_STATUS_ACCENT[status]} />
      </button>
      {popoverOpen && (
        <InviteActionsPopover
          inviteUuid={inviteUuid}
          email={record.email}
          expiresAt={record.invite_expires_at}
          isExpired={isExpired}
          triggerRef={triggerRef}
          onAction={handleAction}
          onClose={() => setPopoverOpen(false)}
        />
      )}
      {/* `inviteUuid` is guaranteed here: the popover disables both actions
          without one, so `confirming` can't be set. */}
      {confirming && inviteUuid && (
        <>
          {confirming === 'resend' ? (
            <ResendInvitePopup invite={{ uuid: inviteUuid, email: record.email }} onClose={() => setConfirming(null)} />
          ) : (
            <RevokeInvitePopup invite={{ uuid: inviteUuid, email: record.email }} onClose={() => setConfirming(null)} />
          )}
        </>
      )}
    </>
  )
}

interface InviteActionsPopoverProps {
  inviteUuid: string | undefined
  email: string
  expiresAt?: string | null
  isExpired: boolean
  triggerRef: React.RefObject<HTMLButtonElement | null>
  onAction: (action: 'resend' | 'revoke') => void
  onClose: () => void
}

// Pure menu: it picks an action and hands off. The mutations themselves live
// in ResendInvitePopup / RevokeInvitePopup, which the pending-invites table
// uses too — so neither surface can drift into its own confirmation rules.
const InviteActionsPopover: React.FC<InviteActionsPopoverProps> = ({
  inviteUuid,
  email,
  expiresAt,
  isExpired,
  triggerRef,
  onAction,
  onClose,
}) => {
  const { t } = useTranslation('org')

  // Portal escapes parent overflow. Outside-click checks BOTH the
  // trigger and the portaled popover.
  const popoverRef = useRef<HTMLDivElement>(null)
  const position = useAnchoredPosition(triggerRef, {
    open: true,
    align: 'left',
    popoverWidth: POPOVER_WIDTH,
    // Lets the hook flip the popover above the trigger when it would not
    // fit below, and clamp it inside the viewport. Without this a row near
    // the bottom of the table opened a popover whose list and footer were
    // cut off, with no way to scroll to them.
    popoverRef,
  })

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node
      const insideTrigger = triggerRef.current?.contains(target)
      const insidePopover = popoverRef.current?.contains(target)
      if (!insideTrigger && !insidePopover) {
        onClose()
      }
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [onClose, triggerRef])

  // Without a UUID there is nothing to act on — the row says "invited" but the
  // invite couldn't be resolved, so both actions stay inert rather than firing
  // a request the backend would reject.
  const disabled = !inviteUuid

  if (!position) return null

  return createPortal(
    <div
      ref={popoverRef}
      // maxHeight is set only when this short action menu cannot fit on
      // either side of the trigger. It has no inner scroll area of its own,
      // so the whole menu scrolls rather than overflowing the viewport.
      style={{ top: position.top, left: position.left, width: POPOVER_WIDTH, maxHeight: position.maxHeight }}
      // z-dropdown for the same reason as NotInvitedCell — a raw z-50 sits
      // below page furniture this menu can overlap.
      className="fixed z-dropdown overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg p-1"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="px-2 pt-1 pb-2 border-b border-gray-100 mb-1">
        <div className="text-xs text-gray-500 truncate">{email}</div>
        {/* The date the admin actually needs: when the link stops working, or
            when it already did. Gated on the expiry actually PARSING, not just
            being non-empty — `formatDate` swallows bad input and returns '',
            which would render a dangling "Expires " with no date. */}
        {!!parseServerDate(expiresAt) && (
          <div className={`text-xs mt-0.5 ${isExpired ? 'text-red-600' : 'text-gray-400'}`}>
            {
              (isExpired
                ? t('invitedCell.expiredOn', { date: formatDate(expiresAt) })
                : t('invitedCell.expiresOn', { date: formatDate(expiresAt) })) as string
            }
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={() => onAction('resend')}
        disabled={disabled}
        className="w-full flex items-center gap-2 px-2 py-1.5 text-sm text-gray-700 hover:bg-gray-50 rounded disabled:opacity-50 disabled:cursor-not-allowed"
        data-qa="invite-cell-resend"
      >
        <IconRotateClockwise size={14} className="text-gray-400" />
        {t('invitedCell.resendLabel') as string}
      </button>
      <button
        type="button"
        onClick={() => onAction('revoke')}
        disabled={disabled}
        className="w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        data-qa="invite-cell-revoke"
      >
        <IconTrash size={14} className="text-gray-400" />
        {t('invitedCell.revokeLabel') as string}
      </button>
    </div>,
    document.body,
  )
}

export default React.memo(InvitedCell)
